import { inject, Service } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { AppToastService } from './app-toast.service';
import {
  MergedText,
  mergeTexts,
  payloadValue,
  readItemWriteConflict,
} from '../utils/item-write-conflict.util';

export type ConditionalWrite<T> = (value: T, previous?: T) => Observable<void>;

export interface TextWrite {
  send: ConditionalWrite<string>;
  local: string;
  previous: string;
  multiline: boolean;
}

export interface PayloadWrite {
  send: ConditionalWrite<unknown>;
  local: unknown;
  previous: unknown;
  kind: 'text' | 'number';
  multiline?: boolean;
}

export interface FailedWriteMerge {
  kind: 'text' | 'number';
  multiline: boolean;
  base: unknown;
  server: unknown;
  local: unknown;
}

export interface LandedWrite<T> {
  landed: T;
  conflicted: boolean;
}

export const MAX_CONFLICT_ATTEMPTS = 3;

type ConflictStep<T> =
  | { kind: 'resend'; value: T; previous: T; merge: MergedText }
  | { kind: 'keep-server'; landed: T };

type ConflictResolver<T> = (
  error: unknown,
  value: T,
  previous: T
) => ConflictStep<T> | null;

@Service()
export class ItemWriteConflictService {
  private readonly toast = inject(AppToastService);

  async writeText(write: TextWrite): Promise<LandedWrite<string>> {
    return this.sendResolvingConflicts(
      write.send,
      write.local,
      write.previous,
      (error, value, previous) =>
        resolveTextConflict(write, error, value, previous)
    );
  }

  async writePayload(write: PayloadWrite): Promise<LandedWrite<unknown>> {
    return this.sendResolvingConflicts(
      write.send,
      write.local,
      write.previous,
      (error, value, previous) =>
        this.resolvePayloadConflict(write, error, value, previous)
    );
  }

  mergeOntoServer(merge: FailedWriteMerge): unknown {
    const base = asText(merge.base);
    const server = asText(merge.server);
    const local = asText(merge.local);
    if (server === base || server === local) return merge.local;
    if (local === base) return merge.server;
    if (merge.kind === 'number') {
      this.toast.showNumberEditDiscarded(local);
      return merge.server;
    }
    const merged = mergeTexts(base, server, local, merge.multiline);
    this.announceMerges([merged]);
    return merged.text;
  }

  private async sendResolvingConflicts<T>(
    send: ConditionalWrite<T>,
    local: T,
    previous: T,
    resolveConflict: ConflictResolver<T>
  ): Promise<LandedWrite<T>> {
    let value = local;
    let base = previous;
    const merges: MergedText[] = [];
    for (let attempt = 1; ; attempt++) {
      try {
        await firstValueFrom(send(value, base));
        if (merges.length > 0) this.announceMerges(merges);
        return { landed: value, conflicted: merges.length > 0 };
      } catch (error) {
        const step =
          attempt < MAX_CONFLICT_ATTEMPTS
            ? resolveConflict(error, value, base)
            : null;
        if (!step) throw error;
        if (step.kind === 'keep-server') {
          return { landed: step.landed, conflicted: true };
        }
        merges.push(step.merge);
        value = step.value;
        base = step.previous;
      }
    }
  }

  private resolvePayloadConflict(
    write: PayloadWrite,
    error: unknown,
    value: unknown,
    previous: unknown
  ): ConflictStep<unknown> | null {
    const conflict = readItemWriteConflict(error);
    if (conflict?.kind !== 'payload') return null;
    if (write.kind === 'number') {
      this.toast.showNumberEditDiscarded(
        String(payloadValue(write.local) ?? '')
      );
      return { kind: 'keep-server', landed: conflict.currentPayload };
    }
    const merge = mergeTexts(
      textOf(previous),
      textOf(conflict.currentPayload),
      textOf(value),
      write.multiline ?? false
    );
    return {
      kind: 'resend',
      value: { value: merge.text },
      previous: conflict.currentPayload,
      merge,
    };
  }

  private announceMerges(merges: MergedText[]): void {
    const discarded = merges.flatMap(merge => merge.discarded);
    if (discarded.length > 0) {
      this.toast.showNumberEditDiscarded(discarded.join(', '));
    } else if (merges.some(merge => merge.overlapped)) {
      this.toast.showTextEditsKeptBoth();
    } else {
      this.toast.showTextEditsMerged();
    }
  }
}

function resolveTextConflict(
  write: TextWrite,
  error: unknown,
  value: string,
  previous: string
): ConflictStep<string> | null {
  const conflict = readItemWriteConflict(error);
  if (conflict?.kind !== 'text') return null;
  const merge = mergeTexts(previous, conflict.current, value, write.multiline);
  return {
    kind: 'resend',
    value: merge.text,
    previous: conflict.current,
    merge,
  };
}

function asText(value: unknown): string {
  return value === null || value === undefined ? '' : String(value);
}

function textOf(payload: unknown): string {
  const value = payloadValue(payload);
  return typeof value === 'string' ? value : '';
}
